import { useState, useMemo, useEffect } from 'react';
import { Box } from '@chakra-ui/react';
import { Button, Colors } from '@mercator-gcp/grout';
import { Company as CompanyProfile } from '@mercator-gcp/types/backend/Company';

import LeadsTable from 'components/elements/LeadsTable';
import StatusDropdownFilter from 'components/elements/projectFilters/StatusDropdownFilter';
import SearchInput from 'components/elements/SearchInput';
import { FlexRow } from 'components/layout/FlexRow';
import { Pagination } from 'components/layout/List/Paging';
import LocationFilter from 'components/widgets/CompanyDetails/CompanyProjects/Filters/LocationFilter';
import CompanyProjectsHeader from 'components/widgets/CompanyDetails/CompanyProjects/Header';
import NoProjectsMatchFiltersMessage from 'components/widgets/CompanyDetails/CompanyProjects/NoProjectsMatchFiltersMessage';
import NoProjectsMessage from 'components/widgets/CompanyDetails/CompanyProjects/NoProjectsMessage';
import { useCompanyProjectsV2 } from 'hooks/useCompanies';
import { useDebounce } from 'hooks/useDebounced';
import { useHiddenProjects } from 'hooks/useHiddenProjects';
import { usePaginate } from 'hooks/usePaginate';
import { useSortedProjects } from 'hooks/useSortedProjects';
import { PaginatedProject } from 'types';
import { ProjectGroupTypes } from 'utils/analytics/constants';
import { calcTotalPages } from 'utils/misc';

const headerStyle = {
  marginBottom: 'padding_24',
};

const filterOptionsStyle = {
  justifyContent: 'space-between',
  alignItems: 'center',
};

const tableContentStyle = {
  width: '100%',
  paddingY: 'padding_24',
};

const clearButtonStyle = {
  padding: '0',
  height: 'auto',
  color: Colors.Tangerine900,
};

const searchStyle = {
  width: '100%',
  maxWidth: '240px',
};

type Props = {
  id: number;
  company?: CompanyProfile;
};

const CompanyProjects = ({ id, company }: Props) => {
  const [eventSelections] = useState<string[]>([]);
  const [regionFilter, setRegionFilter] = useState<string[]>([]);
  const [projectStatusFilter, setProjectStatusFilter] = useState<string[]>([]);
  const [normalizedProjectStatusFilter, setNormalizedProjectStatusFilter] =
    useState<string>('');
  const [keywordFilter, setKeywordFilter] = useState<string>('');
  const debouncedKeywordFilter = useDebounce(keywordFilter, 500);
  const {
    nextPage,
    previousPage,
    page: currentPage,
    setPageNumber,
    resetPagination,
  } = usePaginate();
  const { getAllHiddenProjectIdsForUser } = useHiddenProjects();
  const { data: hiddenProjectsSet } = getAllHiddenProjectIdsForUser;
  const { sortArray } = useSortedProjects(eventSelections);

  const debouncedParams = useDebounce(
    {
      currentPage,
      sortArray,
      regionFilter,
      projectStatusFilter: normalizedProjectStatusFilter,
      keywordFilter: debouncedKeywordFilter,
    },
    300,
  );

  const { data: companyProjects, isLoading: isCompanyProjectsLoading } =
    useCompanyProjectsV2(
      [id],
      debouncedParams.currentPage,
      debouncedParams.sortArray,
      debouncedParams.regionFilter,
      debouncedParams.projectStatusFilter,
      debouncedParams.keywordFilter,
    );

  useEffect(() => {
    resetPagination();
  }, [sortArray, regionFilter, debouncedKeywordFilter]);

  const changeLURDtoEntitlement = (item: PaginatedProject): PaginatedProject =>
    item.city === 'Los Angeles' &&
    item?.latest_activity?.type.toLowerCase() === 'land use redesignation'
      ? {
          ...item,
          latest_activity: {
            ...item?.latest_activity,
            type: item?.latest_activity?.type.replace(
              'Land Use Redesignation',
              'Entitlement',
            ),
          },
        }
      : item;

  const filteredProjects = useMemo<PaginatedProject[]>(() => {
    return (
      companyProjects?.results
        ?.filter((item) => !hiddenProjectsSet?.has(item.slug))
        .map((item) => changeLURDtoEntitlement(item)) || []
    );
  }, [companyProjects?.results, hiddenProjectsSet]);

  const totalPages = useMemo(
    () => calcTotalPages(companyProjects?.count || 0),
    [companyProjects?.count],
  );

  const activeProjects = company?.num_active_projects || 0;
  const completedProjects = company?.num_completed_projects || 0;
  const activeDocuments = company?.num_active_documents || 0;
  const completedDocuments = company?.num_completed_documents || 0;
  const totalProjects =
    (company?.num_total_projects || 0) + (company?.num_total_documents || 0);
  const showNoProjectsMessage = totalProjects === 0;
  const showNoProjectsMatchFiltersMessage =
    totalProjects > 0 && !isCompanyProjectsLoading && !filteredProjects.length;

  const clearAllFilters = () => {
    setRegionFilter([]);
    setProjectStatusFilter([]);
    setKeywordFilter('');
  };

  const onChangeStatusFilter = (value: string[], status: string) => {
    setProjectStatusFilter(value);
    setNormalizedProjectStatusFilter(status);
  };

  const hasActiveFilters = useMemo(() => {
    return (
      regionFilter.length > 0 ||
      projectStatusFilter.length > 0 ||
      keywordFilter.length > 0
    );
  }, [regionFilter, projectStatusFilter, keywordFilter]);

  return (
    <>
      <Box sx={headerStyle}>
        <CompanyProjectsHeader
          activeProjects={activeProjects + activeDocuments}
          completedProjects={completedProjects + completedDocuments}
        />
      </Box>
      <FlexRow sx={filterOptionsStyle}>
        <FlexRow.Left sx={{ gap: 'padding_16' }}>
          <LocationFilter
            value={regionFilter}
            onChangeSelection={(value) => setRegionFilter(value)}
          />
          <StatusDropdownFilter
            value={projectStatusFilter}
            onChangeSelection={onChangeStatusFilter}
          />
          {hasActiveFilters && (
            <Button variant="unstyled" onClick={clearAllFilters} sx={clearButtonStyle}>
              Clear All
            </Button>
          )}
        </FlexRow.Left>
        <FlexRow.Right sx={searchStyle}>
          <SearchInput
            value={keywordFilter}
            onChange={setKeywordFilter}
            placeholder="Search projects by keyword"
          />
        </FlexRow.Right>
      </FlexRow>
      <LeadsTable
        items={filteredProjects}
        leadContext={{
          groupId: String(id),
          groupType: ProjectGroupTypes.COMPANY_PROJECTS,
        }}
        columnProps={{ showStatus: true }}
        ariaLabel="company projects"
        isLoading={isCompanyProjectsLoading}
        footer={
          <Pagination
            currentPage={currentPage}
            isLoading={isCompanyProjectsLoading}
            setPage={(pageNumber) => setPageNumber(pageNumber)}
            totalPages={totalPages}
            onNext={() => nextPage()}
            onPrev={() => previousPage()}
          />
        }
      />

      {showNoProjectsMessage ||
        (showNoProjectsMatchFiltersMessage && (
          <FlexRow sx={tableContentStyle}>
            {showNoProjectsMessage && <NoProjectsMessage />}
            {showNoProjectsMatchFiltersMessage && <NoProjectsMatchFiltersMessage />}
          </FlexRow>
        ))}
    </>
  );
};

export default CompanyProjects;
